import type {
  FlattenedPlaybookNode,
  PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import type { FC } from "react";
import { FeedbackReviewJumpToPopover } from "./feedbackReviewJumpToPopover.js";
import { getAdjacentPlaybookNodeIdWithOpenFeedback } from "./feedbackReviewTreeHelpers.js";

/**
 * Strips the standalone button chrome so the three controls read as one pill,
 * and draws the focus ring inward so the pill's overflow-hidden can't clip it.
 */
const joinedButtonClassName =
  "rounded-none shadow-none ring-0 focus-visible:-outline-offset-2";

export interface FeedbackReviewNavigationProps {
  flattenedNodes: readonly FlattenedPlaybookNode[];
  openFeedbackCounts: ReadonlyMap<PlaybookNodeId, number>;
  selectedNodeId: PlaybookNodeId | null;
  onSelectNode: (nodeId: PlaybookNodeId) => void;
}

/**
 * Moves the review tab between criteria: the arrows step to the next node that
 * still has open feedback (never wrapping), while "Jump to" opens the whole
 * tree.
 */
export const FeedbackReviewNavigation: FC<FeedbackReviewNavigationProps> = (
  props,
) => {
  const { flattenedNodes, openFeedbackCounts, selectedNodeId, onSelectNode } =
    props;

  const previousNodeId = getAdjacentPlaybookNodeIdWithOpenFeedback(
    flattenedNodes,
    openFeedbackCounts,
    selectedNodeId,
    "previous",
  );
  const nextNodeId = getAdjacentPlaybookNodeIdWithOpenFeedback(
    flattenedNodes,
    openFeedbackCounts,
    selectedNodeId,
    "next",
  );

  return (
    <div className="flex shrink-0 items-center gap-2">
      <span className="text-sm text-muted-foreground">Go to criterion</span>
      {/* The Select's display:contents wrapper generates no box, so divide-x
          can't paint around it — the trigger carries its own side borders. */}
      <div className="inline-flex items-center overflow-hidden rounded-lg border border-border-strong bg-white">
        <IconButton
          variant={ButtonVariant.Secondary}
          className={joinedButtonClassName}
          icon={<IconChevronLeft className="size-4" />}
          accessibleLabel="Previous criterion with open feedback"
          isDisabled={previousNodeId === null}
          onClick={() => {
            if (previousNodeId !== null) {
              onSelectNode(previousNodeId);
            }
          }}
        />
        <FeedbackReviewJumpToPopover
          flattenedNodes={flattenedNodes}
          openFeedbackCounts={openFeedbackCounts}
          selectedNodeId={selectedNodeId}
          onSelectNode={onSelectNode}
          isDisabled={flattenedNodes.length === 0}
        />
        <IconButton
          variant={ButtonVariant.Secondary}
          className={joinedButtonClassName}
          icon={<IconChevronRight className="size-4" />}
          accessibleLabel="Next criterion with open feedback"
          isDisabled={nextNodeId === null}
          onClick={() => {
            if (nextNodeId !== null) {
              onSelectNode(nextNodeId);
            }
          }}
        />
      </div>
    </div>
  );
};
